import { Controller } from "@hotwired/stimulus";
import Dropzone from "dropzone";
import "dropzone/dist/dropzone.css"

export default class extends Controller {
  static targets = ["input", "dropArea"]
  static values = {
    existingFile: Object,
    acceptedFiles: { type: String, default: "" },
    maxFilesize: { type: Number, default: 10 },
    maxFiles: { type: Number, default: 1 },
    addRemoveLinks: { type: Boolean, default: true },
    dictRemoveFile: { type: String, default: "Remove file" },
  }

  connect() {
    this.dropzone = new Dropzone(this.element, {
      url: "#",
      method: "POST",
      autoQueue: false,
      autoProcessQueue: false,
      paramName: "file",
      maxFileSize: this.maxFilesizeValue,
      maxFiles: this.maxFilesValue,
      acceptedFiles: this.acceptedFilesValue || null,
      addRemoveLinks: this.addRemoveLinksValue,
      dictRemoveFile: this.dictRemoveFileValue,
      dictDefaultMessage: "Drop a file here",
      autoDiscover: false
    })

    this.setupEventListeners()
    this.loadExistingFile()
  }

  setupEventListeners() {
    // Handle new file upload
    this.dropzone.on('addedfile', (file) => {
      // Skip upload for existing files loaded from the server
      if (this._loadingExisting) return

      this.hideDropArea()

      // Only allow one file
      if (this.dropzone.files.length > 1) {
        this.dropzone.removeFile(this.dropzone.files[0])
      }

      // Get presigned URL and upload
      fetch('/files/cache/presign')
        .then(response => response.json())
        .then(json => {
          file.uploadURL = json.url
          file.additionalData = json.fields
          this.dropzone.processFile(file)
        })
    })

    this.dropzone.on('processing', (file) => {
      this.dropzone.options.url = file.uploadURL
    })

    this.dropzone.on('sending', (file, xhr, formData) => {
      for (let field in file.additionalData) {
        formData.append(field, file.additionalData[field])
      }
    })

    this.dropzone.on('success', (file) => {
      const fileData = {
        id: file.additionalData.key.match(/cache\/(.+)/)[1],
        storage: 'cache',
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type
        }
      }

      this.inputTarget.value = JSON.stringify(fileData)

      // Trigger validation in parent form
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    })

    this.dropzone.on('removedfile', () => {
      this.inputTarget.value = ''
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))

      if (this.dropzone.files.length === 0) {
        this.showDropArea()
      }
    })
  }

  hideDropArea() {
    if (this.hasDropAreaTarget) this.dropAreaTarget.classList.add('hidden')
  }

  showDropArea() {
    if (this.hasDropAreaTarget) this.dropAreaTarget.classList.remove('hidden')
  }

  loadExistingFile() {
    if (this.hasExistingFileValue && this.existingFileValue.filename) {
      this.hideDropArea()
      this._loadingExisting = true
      const mockFile = {
        name: this.existingFileValue.filename,
        size: this.existingFileValue.size,
        type: this.existingFileValue.mime_type,
        accepted: true
      }

      this.dropzone.emit("addedfile", mockFile)

      // If it's an image, show thumbnail
      if (this.existingFileValue.url && mockFile.type?.startsWith('image/')) {
        this.dropzone.emit("thumbnail", mockFile, this.existingFileValue.url)
      }

      this.dropzone.emit("complete", mockFile)

      // Mark as existing so we don't remove it on removedfile
      mockFile.status = Dropzone.SUCCESS
      mockFile.previewElement.classList.add('dz-existing')

      this._loadingExisting = false
    }
  }

  disconnect() {
    if (this.dropzone) {
      this.dropzone.destroy()
    }
  }
}